import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useQuery } from '@tanstack/react-query'

import { useAuthStatus } from '@jotta/auth-client/useAuthStatus'
import { useFeatures } from '@jotta/grpc-connect-client/config'
import { ItemKind } from '@jotta/grpc-js-client/fileService'
import { getDownloadLink } from '@jotta/grpc-js-client/fileService'
import { isWopi } from '@jotta/grpc-js-client/wopiService'
import { AppError, handleUnknownError } from '@jotta/types/AppError'
import type { PathItemObject } from '@jotta/types/Files'
import { Box } from '@jotta/ui/Box'
import { selectCodeView } from '@jotta/ui/CodeView'

import { FileItemTypeIcon } from '../FileItemTypeIcon/FileItemTypeIcon'

import { FileCodeView } from './FileCodeView/FileCodeView'
import { FileTextView } from './FileCodeView/FileTextView'
import { FileViewAudio } from './FileViewAudio/FileViewAudio'
import { FileViewImage } from './FileViewImage/FileViewImage'
import { FileViewVideo } from './FileViewVideo/FileViewVideo'
import { PdfView } from './PdfView/PdfView'
import { WopiViewContainer } from './WopiView/WopiViewContainer'

const debug = Debug('jotta:ui:FileView')

interface FilePdfViewProps {
  file: PathItemObject
  forcePdfJs?: boolean
  isActive?: boolean
  fetchDownloadLink?: typeof getDownloadLink
  token?: string
}

export const FilePdfView = observer<FilePdfViewProps>(function FilePdfView({
  file,
  forcePdfJs = false,
  isActive = false,
  fetchDownloadLink = getDownloadLink,
  token,
}) {
  const auth = useAuthStatus()
  const accessToken =
    file.accessTokenOverride || token || auth.data?.accessToken || ''
  let downloadLink = createPdfUrl(file, accessToken)
  if (isActive) {
    debug(downloadLink.length, downloadLink)
  }
  const query = useQuery({
    enabled: isActive && !downloadLink,
    queryKey: ['downloadLink', file.path, !!file.deletedAtMillis] as const,
    retry: false,
    queryFn: async () => {
      debug('Missing downloadLink, fetching...')
      const downloadLink = await fetchDownloadLink({
        path: file.path,
        includeDeleted: !!file.deletedAtMillis,
      })
      const pdfUrl = createPdfUrl(
        {
          ...file,
          downloadLink,
        },
        accessToken,
      )
      if (!pdfUrl) {
        throw new AppError({
          message: 'Failed to create PDF url',
        })
      }
      const res = await fetch(pdfUrl, {
        method: 'GET',
      })
      if (!res.ok) {
        throw new AppError({
          message: `Invalid PDF url ${pdfUrl} ${res.status}`,
          HTTPStatus: res.status,
        })
      }
      return downloadLink
    },
    throwOnError: false,
  })
  if (query.data) {
    downloadLink = query.data
  }
  if (downloadLink) {
    return <PdfView url={downloadLink} />
  }
  if (query.error) {
    return <p>{handleUnknownError(query.error).message}</p>
  }
  return null
})

function createPdfUrl(file: PathItemObject, token: string) {
  if (!file.downloadLink) {
    return ''
  }
  if (file.downloadLink.startsWith('/')) {
    if (file.downloadLink.endsWith('/')) {
      return file.downloadLink + file.name
    }
    return `${file.downloadLink}/${file.name}`
  }
  const url = new URL(file.downloadLink)
  url.pathname = `${url.pathname}/${encodeURIComponent(file.name)}`
  debug('URL token', url.searchParams.get('access_token')?.substring(0, 32))

  if (!url.searchParams.has('access_token')) {
    url.searchParams.set('access_token', token)
  }
  url.searchParams.set('inlineOverride', 'true')
  url.searchParams.set('contentOverride', 'application/pdf')

  return url.href
}

export interface FileViewProps {
  file: PathItemObject
  token?: string
  isActive?: boolean
}

export const FileView = observer<FileViewProps>(function FileView({
  file,
  token,
  isActive = false,
}) {
  const features = useFeatures()

  if (isWopi(file.name, features.wopiTestMode)) {
    if (!isActive) {
      return null
    }
    return <WopiViewContainer item={file} />
  }

  const fileIconElement = (
    <FileItemTypeIcon
      sx={{
        variant: 'layout.center',
      }}
      size={'50%'}
      item={file}
    />
  )

  switch (file.kind) {
    case ItemKind.PDF:
      return <FilePdfView file={file} isActive={isActive} token={token} />
    case ItemKind.IMAGE:
      return (
        <FileViewImage
          item={file}
          size="2480x"
          fallback={fileIconElement}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
          }}
        />
      )
    case ItemKind.VIDEO: {
      return <FileViewVideo file={file} token={token} enabled={isActive} />
    }
    case ItemKind.AUDIO:
      return <FileViewAudio file={file} token={token} />
  }
  const view = selectCodeView(file)
  switch (view.view) {
    case 'CODE':
      return (
        <Box variant="layout.center">
          {isActive && (
            <FileCodeView token={token} language={view.lang} file={file} />
          )}
        </Box>
      )
    case 'TEXT':
      return (
        <Box variant="layout.center">
          {isActive && <FileTextView token={token} file={file} />}
        </Box>
      )

    default:
      return fileIconElement
  }
})
